import {
  ReasoningEffort,
  MODEL_REASONING_MAP,
} from "../../question-input-container/ModelSettingsPopup/ModelSettingsPopup";

export type PromptExample = {
  input: string;
  output: string;
};

export type AnswerMode = {
  // -- Identity & UI --
  id: string; // Unique identifier
  name: string;
  color: string;
  icon: string;
  orderIndex: number; // For drag & drop sorting
  isCustom?: boolean; // Distinguish static vs. user-created
  disable?: boolean;

  // -- Visibility Logic --
  // 'any' = always visible
  // 'documents' = only when files are uploaded
  // 'no_documents' = only when no files are uploaded
  contextRequirement: "any" | "documents" | "no_documents";

  // -- Model Parameters --
  mode: string;
  model: string;
  reasoningEffort: string;

  // -- Prompt Context --
  placeholder: string;
  documentMode: boolean; // Backend flag: trigger RAG logic
  default_question?: string;
  description?: string;

  // -- Custom Prompt Definition --
  roleDefinition?: string;
  taskDescription?: string;
  outputFormat?: string;
  examples?: PromptExample[];
};

/**
 * Static Answer Modes available to all users.
 */
export const StaticAnswerModes: AnswerMode[] = [
  {
    id: "standard",
    name: "Standard",
    color: "#00a66d",
    icon: "Sparkle20Regular",
    orderIndex: 0,
    contextRequirement: "any",
    mode: "standard",
    model: "gpt-6.1-sol",
    reasoningEffort: MODEL_REASONING_MAP["gpt-6.1-sol"][ReasoningEffort.Normal],
    placeholder: "How can I help you today?",
    documentMode: true, // Will use RAG if docs are present, otherwise generic
  },
  {
    id: "writing",
    name: "Writing Assistant",
    color: "#00a66d",
    icon: "CalligraphyPen20Regular",
    orderIndex: 1,
    contextRequirement: "any",
    mode: "writing",
    model: "gpt-6.1-sol",
    reasoningEffort: MODEL_REASONING_MAP["gpt-6.1-sol"][ReasoningEffort.Normal],
    placeholder: "Draft an email, a report, or notes...",
    documentMode: true,
  },
  {
    id: "code",
    name: "Coding",
    color: "#00a66d",
    icon: "Code20Regular",
    orderIndex: 2,
    contextRequirement: "any",
    mode: "code",
    model: "gpt-6.1-sol",
    reasoningEffort: MODEL_REASONING_MAP["gpt-6.1-sol"][ReasoningEffort.Normal],
    placeholder: "Write code, debug, or refactor...",
    documentMode: true,
  },
  {
    id: "compare",
    name: "Compare",
    color: "#f07000", // MB Orange for visibility
    icon: "ArrowSwap20Regular",
    orderIndex: 3,
    contextRequirement: "documents", // ONLY visible with files
    mode: "compare",
    model: "gpt-6.1-sol",
    reasoningEffort: MODEL_REASONING_MAP["gpt-6.1-sol"][ReasoningEffort.Thinking],
    placeholder: "What should I compare in these documents?",
    documentMode: true,
  },
  {
    id: "summarize",
    name: "Summary",
    color: "#00a66d", // MB Dark Green
    icon: "TextDescription20Regular",
    orderIndex: 4,
    contextRequirement: "any", // ONLY visible with files
    mode: "summarize",
    model: "gpt-6.1-sol",
    reasoningEffort: MODEL_REASONING_MAP["gpt-6.1-sol"][ReasoningEffort.Thinking],
    placeholder: "Summarize specific parts or themes...",
    documentMode: true,
  },
  {
    id: "detailed",
    name: "Deep Dive",
    color: "#00a66d", // MB Dark Green
    icon: "LightbulbFilament20Regular",
    orderIndex: 4,
    contextRequirement: "any", // ONLY visible with files
    mode: "detailed",
    model: "gpt-6.1-sol",
    reasoningEffort: MODEL_REASONING_MAP["gpt-6.1-sol"][ReasoningEffort.Thinking],
    placeholder: "Summarize specific parts or themes...",
    documentMode: true,
  },
];

/**
 * Creates a default template for a new custom Answer Mode.
 */
export const defaultAnswerMode = (
  overrides: Partial<AnswerMode>,
): AnswerMode => {
  return {
    id: "standard",
    name: "Standard",
    color: "#00a66d",
    icon: "Sparkle20Regular",
    orderIndex: 0,
    contextRequirement: "any",
    mode: "standard",
    model: "gpt-6.1-sol",
    reasoningEffort: MODEL_REASONING_MAP["gpt-6.1-sol"][ReasoningEffort.Normal],
    placeholder: "How can I help you today?",
    documentMode: true, // Will use RAG if docs are present, otherwise generic
    ...overrides,
  };
};
